import React from "react";
import { Coins, FileText, User, Car, TrendingUp, TrendingDown } from "lucide-react";
import { fmtEUR } from "../../lib/api";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const card = "rounded-2xl border border-[#D4A72C]/20 bg-[#0A0907]/90";

const Kpi = ({ icon: Icon, label, value, sub, testid, children }) => (
  <div className={`${card} p-5 flex gap-4 items-start`} data-testid={testid}>
    <Icon className="text-[#E9C46A] shrink-0 mt-1" size={26} />
    <div className="min-w-0">
      <div className="text-sm text-slate-400">{label}</div>
      <div className="font-display text-3xl text-white mt-1" data-testid={`${testid}-value`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
      {children}
    </div>
  </div>
);

export function EvKpis({ s }) {
  const prev = s.previous_total || 0;
  const pct = prev > 0 ? Math.round(((s.total - prev) / prev) * 100) : null;
  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4" data-testid="ev-kpis">
      <Kpi icon={Coins} label="Totale ricariche mese" value={fmtEUR(s.total)} testid="ev-kpi-total">
        {pct != null && <div className={`text-[11px] mt-1 inline-flex items-center gap-1 ${pct >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{pct >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{pct >= 0 ? "+" : ""}{pct}% rispetto al mese precedente</div>}
      </Kpi>
      <Kpi icon={FileText} label="Numero ricariche" value={s.count} testid="ev-kpi-count" />
      <Kpi icon={User} label="Autisti" value={s.drivers_count} sub="con almeno una ricarica" testid="ev-kpi-drivers" />
      <Kpi icon={Car} label="Veicoli" value={s.vehicles_count} sub="elettrici ricaricati" testid="ev-kpi-vehicles" />
    </div>
  );
}

const Bars = ({ title, icon: Icon, rows, testid }) => {
  const max = Math.max(...rows.map((r) => r.total), 1);
  return (
    <div className={`${card} p-5`} data-testid={testid}>
      <div className="font-display text-lg text-white inline-flex items-center gap-2 mb-4"><Icon size={18} className="text-[#E9C46A]" />{title}</div>
      {!rows.length && <div className="text-sm text-slate-500">Nessuna ricarica nel periodo</div>}
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.key} className="grid grid-cols-[1fr_auto] gap-x-3 items-center text-sm" data-testid={`${testid}-row`}>
            <span className="truncate text-slate-200">{r.name}</span>
            <span className="font-mono-num text-white">{fmtEUR(r.total)}</span>
            <div className="col-span-2 h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#8A6A1F] to-[#E9C46A]" style={{ width: `${(r.total / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
};

export function EvSidePanels({ s }) {
  const max = Math.max(...s.trend.map((t) => t.total), 1);
  return (
    <div className="space-y-4">
      <Bars title="Ricariche per autista" icon={User} testid="ev-by-driver" rows={s.by_driver.map((d) => ({ key: d.driver_id, name: d.name, total: d.total }))} />
      <Bars title="Ricariche per veicolo" icon={Car} testid="ev-by-vehicle" rows={s.by_vehicle.map((v) => ({ key: v.vehicle_id, name: `${v.label} · ${v.plate}`, total: v.total }))} />
      <div className={`${card} p-5`} data-testid="ev-trend">
        <div className="font-display text-lg text-white mb-4">Totali mensili</div>
        <div className="flex items-end gap-2 h-32">
          {s.trend.map((t, i) => (
            <div key={`${t.year}-${t.month}`} className="flex-1 flex flex-col items-center gap-1 h-full justify-end" title={fmtEUR(t.total)}>
              <span className="text-[9px] font-mono-num text-slate-400">{t.total ? Math.round(t.total) : ""}</span>
              <div className={`w-full rounded-t ${i === s.trend.length - 1 ? "bg-gradient-to-t from-[#B8892A] to-[#F1D27A]" : "bg-[#3A2F17]"}`} style={{ height: `${Math.max((t.total / max) * 100, 2)}%` }} />
              <span className="text-[10px] text-slate-500">{MONTHS[t.month - 1]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
